import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function getInitials(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed || trimmed === '...') return '…';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

export interface SidebarAccountMenuProps {
  displayName: string;
  loading?: boolean;
  portalLabel: string;
  /** Tailwind bg class for avatar circle, e.g. bg-[#E8621A] */
  avatarClassName?: string;
  onChangePassword: () => void;
  onSignOut: () => void;
  /** Called after a menu action (e.g. close mobile drawer). */
  onAfterAction?: () => void;
}

export default function SidebarAccountMenu({
  displayName,
  loading,
  portalLabel,
  avatarClassName = 'bg-[#E8621A]',
  onChangePassword,
  onSignOut,
  onAfterAction,
}: SidebarAccountMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const initials = loading ? '…' : getInitials(displayName);

  const runAction = (action: () => void) => {
    setOpen(false);
    action();
    onAfterAction?.();
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors cursor-pointer text-left"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${avatarClassName}`}
        >
          <span className="text-white text-[11px] font-bold leading-none">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-medium truncate">
            {loading ? '...' : displayName}
          </div>
          <div className="text-white/40 text-[11px] truncate">{portalLabel}</div>
        </div>
        <div
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
          aria-hidden="true"
        >
          <i className={`ri-more-2-fill text-white/50 text-sm transition-transform ${open ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 right-0 bottom-full mb-2 z-50 bg-[#2A2A2A] border border-white/10 rounded-xl overflow-hidden shadow-xl"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => runAction(onChangePassword)}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-white/80 hover:bg-white/5 hover:text-white cursor-pointer transition-colors whitespace-nowrap border-b border-white/5"
          >
            <i className="ri-lock-password-line text-base" />
            {t('nav.changePassword')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => runAction(onSignOut)}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-white/80 hover:bg-white/5 hover:text-white cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line text-base" />
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
